/* eslint-disable no-underscore-dangle */
import * as LDClient from 'launchdarkly-js-client-sdk'

import Mutex from '../helpers/Mutex'
import Logger from '../util/logger'
import { IDENTITY_SDK_VERSION } from '../version'
import Observable from '../observable'

const IDENTITY_OVERRIDES_KEY = 'identity_ld_overrides'

export default class WebRemoteConfigProvider extends Observable {
  #brand

  #product

  _logger

  _client

  _ready = false

  #crossDomain = false

  _allFlags = {}

  #overrides = {}

  #key

  #checkoutEnabled = false

  customProperties = {}

  #mutex = new Mutex()

  constructor(key, brand, product, debug, checkoutEnabled, crossDomain) {
    super()

    this._logger = new Logger('BrandWebRemoteConfigProvider', debug)

    this.#key = key
    this.#brand = brand
    this.#product = product
    this.#crossDomain = crossDomain
    this.#checkoutEnabled = checkoutEnabled

    const savedOverrides = localStorage.getItem(IDENTITY_OVERRIDES_KEY)
    if (savedOverrides) {
      this.#overrides = WebRemoteConfigProvider.pruneOverrides(JSON.parse(savedOverrides))
    }
  }

  static pruneOverrides(overrides = {}) {
    const prunedOverrides = {}
    Object.entries(overrides).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        prunedOverrides[key] = value
      }
    })
    return prunedOverrides
  }

  get isReady() {
    return this._ready
  }

  get #allFlags() {
    return this._allFlags
  }

  set #allFlags(flags) {
    // If the flags haven't changed, just ignore it
    if (JSON.stringify(this._allFlags) === JSON.stringify(flags)) {
      return
    }

    this._allFlags = flags
    this.notify('change', flags)
  }

  getBoolean(name, defaultValue) {
    if (!this._ready) {
      this._logger.log(`Trying to get '${name}' value before ready remote config is ready`)
      return defaultValue
    }

    return this.#flagsWithOverrides[name] ?? defaultValue
  }

  getString(name, defaultValue) {
    if (!this._ready) {
      this._logger.log(`Trying to get '${name}' value before ready remote config is ready`)
      return defaultValue
    }

    return this.#flagsWithOverrides[name] ?? defaultValue
  }

  getInt(name, defaultValue) {
    if (!this._ready) {
      this._logger.log(`Trying to get '${name}' value before ready remote config is ready`)
      return defaultValue
    }

    return this.#flagsWithOverrides[name] ?? defaultValue
  }

  getDouble(name, defaultValue) {
    if (!this._ready) {
      this._logger.log(`Trying to get '${name}' value before ready remote config is ready`)
      return defaultValue
    }

    return this.#flagsWithOverrides[name] ?? defaultValue
  }

  getJson(name) {
    if (!this._ready) {
      this._logger.log(`Trying to get '${name}' value before ready remote config is ready`)
      return null
    }
    return JSON.stringify(this.#flagsWithOverrides[name])
  }

  allFlags() {
    const allFlags = this.#flagsWithOverrides
    return Object.keys(allFlags).reduce((acc, key) => {
      if (allFlags[key] === null || allFlags[key] === undefined) return acc
      return { ...acc, [key]: allFlags[key] }
    }, {})
  }

  allFlagsWithoutOverrides() {
    return this.#allFlags
  }

  allOverrides() {
    return this.#overrides
  }

  resetOverrides() {
    this.#overrides = {}
    localStorage.removeItem(IDENTITY_OVERRIDES_KEY)
  }

  setOverrides(overrides) {
    const prunedOverrides = WebRemoteConfigProvider.pruneOverrides(overrides)
    this.#overrides = { ...this.#overrides, ...prunedOverrides }
    localStorage.setItem(IDENTITY_OVERRIDES_KEY, JSON.stringify(this.#overrides))
  }

  updateIdentity(checkoutEnabled = this.#checkoutEnabled) {
    return this.#mutex.withLock(async () => {
      this.#checkoutEnabled = checkoutEnabled
      if (this._client) {
        await this.#updateIdentity()
      } else {
        await this.#initializeClient()
      }
    }).catch((e) => this._logger.log('Failed to update identity:', e))
  }

  async reconfigure(key, brand, product, debug, checkoutEnabled, crossDomain) {
    const needsRecreate = this._client && this.#key !== key

    this.#key = key
    this.#brand = brand
    this.#product = product
    this.#crossDomain = crossDomain
    this.#checkoutEnabled = checkoutEnabled

    if (needsRecreate) {
      await this.#mutex.withLock(async () => {
        await this._client?.close()
        this._client = null

        this._ready = false
        this._allFlags = {}

        await this.#initializeClient()
      })
    }
  }

  #initializeClient() {
    return new Promise((resolve) => {
      const user = this.#buildLDUser()
      this._logger.log('Initializing LaunchDarkly with this user information:', user)
      this._client = LDClient.initialize(this.#key, user, this._getInitializationOptions())

      this._client.on('ready', () => {
        this._ready = true
        this._logger.log('LaunchDarkly Ready')
        this.#allFlags = this._client.allFlags()
        this.notify('ready', this.#allFlags)
        resolve()
      })

      this._client.on('change', (changes) => {
        this._logger.log('LaunchDarkly flags have changed! Updating local values.')
        this.#allFlags = Object.keys(changes)
          .reduce((acc, key) => {
            const value = changes[key].current

            // If the value isv alid
            if (value) {
              return {
                ...acc,
                [key]: value,
              }
            }

            delete acc[key]
            return acc
          }, { ...this.#allFlags })
      })
    })
  }

  async #updateIdentity() {
    return new Promise((resolve) => {
      const user = this.#buildLDUser()
      const currentLDUser = this._client.getContext()

      if (JSON.stringify(user) === JSON.stringify(currentLDUser)) {
        this._logger.log('User identity is the same, no update needed')
        resolve()
        return
      }

      this._logger.log('Updating user identity to', user)

      this._client.identify(user)
        .then((newFlags) => {
          this._logger.log('User identity updated successfully')
          this.#allFlags = newFlags
          resolve()
        })
    })
  }

  #buildLDUser() {
    const currentUser = window.mParticle?.Identity?.getCurrentUser
      ? window.mParticle.Identity.getCurrentUser() : null

    let mpid = currentUser?.getMPID()
    if (!mpid) mpid = 'unknown'

    return {
      kind: 'user',
      'device-os': 'Web',
      'idm-sdk-version': IDENTITY_SDK_VERSION,
      key: mpid,
      brand: this.#brand,
      product: this.#product,
      crossDomain: this.#crossDomain,
      checkoutEnabled: this.#checkoutEnabled,
    }
  }

  _getInitializationOptions() {
    return {
      logger: {
        debug: () => {},
        info: () => {},
        warn: (message) => this._logger.warn('LD Warning:', message),
        error: (message) => this._logger.warn('LD Error:', message),
      },
    }
  }

  get #flagsWithOverrides() {
    return {
      ...this.#allFlags,
      ...this.#overrides,
    }
  }
}
